import api from './api';

export interface NotificationSettings {
  notificacoes_push: boolean;
  horario_inicio_notificacoes: string;
  horario_fim_notificacoes: string;
  nao_enviar_notificacoes_finais_semana: boolean;
  nao_enviar_notificacoes_feriados: boolean;
  envio_mensagens: boolean;
  notificacoes_lembretes: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

class NotificationConfigService {
  
  /**
   * Buscar todas as configurações de notificação
   */
  async getNotificationSettings(): Promise<NotificationSettings> {
    try {
      const response = await api.get('/notification-config') as any;
      
      // O interceptor já retorna response.data, então response já é ApiResponse
      if (response.success) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao buscar configurações');
    } catch (error: any) {
      console.error('❌ Erro ao buscar configurações de notificação:', error);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao buscar configurações');
    }
  }

  /**
   * Atualizar configurações de notificação
   */
  async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    try {
      const response = await api.put('/notification-config', settings) as any;
      
      if (response.success) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao atualizar configurações');
    } catch (error: any) {
      console.error('Erro ao atualizar configurações de notificação:', error);
      throw new Error(error.response?.data?.message || 'Erro ao atualizar configurações');
    }
  }

  /**
   * Buscar configuração específica
   */
  async getNotificationSetting(key: keyof NotificationSettings): Promise<boolean> {
    try {
      const response = await api.get(`/notification-config/${key}`) as any;
      
      if (response.success) {
        return response.data.value;
      }
      
      throw new Error(response.message || 'Erro ao buscar configuração');
    } catch (error: any) {
      console.error(`Erro ao buscar configuração ${key}:`, error);
      throw new Error(error.response?.data?.message || 'Erro ao buscar configuração');
    }
  }

  /**
   * Atualizar configuração específica
   */
  async updateNotificationSetting(key: keyof NotificationSettings, value: boolean | string): Promise<void> {
    try {
      const response = await api.put(`/notification-config/${key}`, { value }) as any;
      
      if (!response.success) {
        throw new Error(response.message || 'Erro ao atualizar configuração');
      }
    } catch (error: any) {
      console.error(`❌ Erro ao atualizar configuração ${key}:`, error);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao atualizar configuração');
    }
  }

  /**
   * Verificar se alguma notificação está habilitada
   */
  async hasAnyNotificationEnabled(): Promise<boolean> {
    try {
      const response = await api.get('/notification-config/status/enabled') as any;
      
      if (response.success) {
        return response.data.hasAnyEnabled;
      }
      
      throw new Error(response.message || 'Erro ao verificar status');
    } catch (error: any) {
      console.error('Erro ao verificar status das notificações:', error);
      throw new Error(error.response?.data?.message || 'Erro ao verificar status');
    }
  }

  /**
   * Inicializar configurações padrão
   */
  async initializeDefaultSettings(): Promise<void> {
    try {
      const response = await api.post('/notification-config/initialize') as any;
      
      if (!response.success) {
        throw new Error(response.message || 'Erro ao inicializar configurações');
      }
    } catch (error: any) {
      console.error('Erro ao inicializar configurações padrão:', error);
      throw new Error(error.response?.data?.message || 'Erro ao inicializar configurações');
    }
  }
}

export const notificationConfigService = new NotificationConfigService();
export default notificationConfigService; 